import type {ConflictMergeConditionPayload} from '../../page-data/payloads/merge-box'
import type {AdvisoryWorkspace, ConflictsSectionStatuses, MergeStateStatus} from '../../types'
import {getConflictsCondition, getStackCondition} from '../json-api-helpers'
import {BaseSectionStatus} from './base-section-status'

export class ConflictsSectionStatus extends BaseSectionStatus<ConflictsSectionStatuses> {
  #conflictsCondition: ConflictMergeConditionPayload | undefined

  override get shouldRender() {
    // Stacked pull requests surface conflicts through `StacksMergeConflictsAndRebaseSection`
    // instead, so this section should not render for them.
    if (this.#isStacked) {
      return false
    }

    if (this.pullRequest.mergeStateStatus === 'BLOCKED' && !this.pullRequest.viewerCanUpdateBranch) return false
    if (!this.conflictsCondition) return false

    return true
  }

  override get sectionStatus() {
    if (!this.conflictsCondition) return 'PENDING'

    return this.#calculateConflictsState(
      this.pullRequest.advisoryWorkspace,
      this.pullRequest.mergeStateStatus,
      this.conflictsCondition,
      this.#conflicts,
    )
  }

  override get mergeBoxStatus() {
    switch (this.sectionStatus) {
      case 'NO_CONFLICTS':
      case 'HAS_ADVISORY_WORKSPACE':
        return 'PASSED'
      case 'HAS_CONFLICTS':
      case 'HAS_REBASE_CONFLICTS':
      case 'OUT_OF_DATE':
        return 'NEUTRAL'
      case 'PENDING':
      default:
        return 'PENDING'
    }
  }

  get conflictsCondition() {
    if (!this.#conflictsCondition) {
      this.#conflictsCondition = getConflictsCondition(this.mergeRequirements)
    }
    return this.#conflictsCondition
  }

  // Added to expose this to the overview page
  get isStacked() {
    return this.#isStacked
  }

  get #isStacked() {
    return getStackCondition(this.mergeRequirements)?.isStacked ?? false
  }

  get #conflicts() {
    return this.conflictsCondition?.conflicts ?? []
  }

  /**
   * Calculate the state the conflicts section is in.
   */
  #calculateConflictsState = (
    advisoryWorkspace: AdvisoryWorkspace,
    mergeStateStatus: MergeStateStatus,
    conflictsCondition: ConflictMergeConditionPayload,
    conflicts: string[],
  ): ConflictsSectionStatuses => {
    if (mergeStateStatus === 'BEHIND') {
      return 'OUT_OF_DATE'
    } else if (mergeStateStatus === 'UNKNOWN') {
      return 'PENDING'
    } else if (conflictsCondition.result === 'FAILED' && conflicts.length === 0) {
      return 'HAS_REBASE_CONFLICTS'
    } else if (
      !advisoryWorkspace &&
      (mergeStateStatus === 'CLEAN' || mergeStateStatus === 'UNSTABLE' || mergeStateStatus === 'HAS_HOOKS')
    ) {
      return 'NO_CONFLICTS'
    } else if (
      advisoryWorkspace &&
      (mergeStateStatus === 'CLEAN' || mergeStateStatus === 'UNSTABLE' || mergeStateStatus === 'HAS_HOOKS')
    ) {
      return 'HAS_ADVISORY_WORKSPACE'
    } else if (mergeStateStatus === 'DIRTY' && conflictsCondition.result === 'FAILED') {
      return 'HAS_CONFLICTS'
    } else if (mergeStateStatus === 'BLOCKED') {
      return 'OUT_OF_DATE'
    } else {
      // Fallback to PENDING
      return 'PENDING'
    }
  }
}
